import { doc, getDoc, onSnapshot, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  isPublished: boolean;
  order: number;
  isHelpful?: number; // Helpful votes count
  isNotHelpful?: number; // Not helpful votes count
  views?: number; // View count
  createdAt?: Date;
  updatedAt?: Date;
}

export interface FAQConfig {
  faqs: FAQ[];
  lastUpdated?: Date;
}

/**
 * Get FAQ configuration from Firestore (only published FAQs)
 */
export const getFAQConfig = async (): Promise<FAQConfig> => {
  try {
    const faqRef = doc(db, 'config', 'faqs');
    const faqDoc = await getDoc(faqRef);
    
    if (!faqDoc.exists()) {
      return { faqs: [] };
    }
    
    const data = faqDoc.data();
    const faqs: FAQ[] = [];
    
    if (data.faqs && Array.isArray(data.faqs)) {
      data.faqs.forEach((faq: any) => {
        // Only include published FAQs for customers
        if (faq.isPublished) {
          faqs.push({
            id: faq.id || '',
            question: faq.question || '',
            answer: faq.answer || '',
            category: faq.category || 'General',
            isPublished: faq.isPublished ?? true,
            order: faq.order ?? 0,
            isHelpful: faq.isHelpful || 0,
            isNotHelpful: faq.isNotHelpful || 0,
            views: faq.views || 0,
            createdAt: faq.createdAt?.toDate ? faq.createdAt.toDate() : new Date(),
            updatedAt: faq.updatedAt?.toDate ? faq.updatedAt.toDate() : new Date()
          });
        }
      });
    }
    
    return {
      faqs: faqs.sort((a, b) => a.order - b.order),
      lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
    };
  } catch (error) {
    console.error('Error fetching FAQ config:', error);
    return { faqs: [] };
  }
};

/**
 * Subscribe to FAQ configuration changes (only published FAQs)
 */
export const subscribeToFAQConfig = (
  callback: (config: FAQConfig) => void
): (() => void) => {
  const faqRef = doc(db, 'config', 'faqs');
  
  return onSnapshot(faqRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const faqs: FAQ[] = [];
      
      if (data.faqs && Array.isArray(data.faqs)) {
        data.faqs.forEach((faq: any) => {
          // Only include published FAQs for customers
          if (faq.isPublished) {
            faqs.push({
              id: faq.id || '',
              question: faq.question || '',
              answer: faq.answer || '',
              category: faq.category || 'General',
              isPublished: faq.isPublished ?? true,
              order: faq.order ?? 0,
              isHelpful: faq.isHelpful || 0,
              isNotHelpful: faq.isNotHelpful || 0,
              views: faq.views || 0,
              createdAt: faq.createdAt?.toDate ? faq.createdAt.toDate() : new Date(),
              updatedAt: faq.updatedAt?.toDate ? faq.updatedAt.toDate() : new Date()
            });
          }
        });
      }
      
      callback({
        faqs: faqs.sort((a, b) => a.order - b.order),
        lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
      });
    } else {
      callback({ faqs: [] });
    }
  }, (error) => {
    console.error('Error subscribing to FAQ config:', error);
    callback({ faqs: [] });
  });
};

/**
 * Track when a user views an FAQ (clicks to see the answer)
 */
export const trackFAQView = async (faqId: string): Promise<void> => {
  try {
    const faqRef = doc(db, 'config', 'faqs');
    const faqDoc = await getDoc(faqRef);
    
    if (!faqDoc.exists()) {
      return;
    }
    
    const data = faqDoc.data();
    if (data.faqs && Array.isArray(data.faqs)) {
      const updatedFAQs = data.faqs.map((faq: any) => {
        if (faq.id === faqId) {
          return {
            ...faq,
            views: (faq.views || 0) + 1,
            updatedAt: new Date()
          };
        }
        return faq;
      });
      
      await updateDoc(faqRef, {
        faqs: updatedFAQs,
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.error('Error tracking FAQ view:', error);
  }
};

/**
 * Track when a user finds an FAQ helpful
 */
export const markFAQHelpful = async (faqId: string, isHelpful: boolean): Promise<void> => {
  try {
    const faqRef = doc(db, 'config', 'faqs');
    const faqDoc = await getDoc(faqRef);
    
    if (!faqDoc.exists()) {
      return;
    }
    
    const data = faqDoc.data();
    if (data.faqs && Array.isArray(data.faqs)) {
      const updatedFAQs = data.faqs.map((faq: any) => {
        if (faq.id === faqId) {
          return {
            ...faq,
            isHelpful: isHelpful ? (faq.isHelpful || 0) + 1 : (faq.isHelpful || 0),
            isNotHelpful: !isHelpful ? (faq.isNotHelpful || 0) + 1 : (faq.isNotHelpful || 0),
            updatedAt: new Date()
          };
        }
        return faq;
      });
      
      await updateDoc(faqRef, {
        faqs: updatedFAQs,
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.error('Error marking FAQ helpful:', error);
  }
};

/**
 * Group FAQs by category
 */
export const groupFAQsByCategory = (faqs: FAQ[]) => {
  const grouped = faqs.reduce((acc, faq) => {
    const category = faq.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);
  
  // Sort FAQs within each category by order
  Object.keys(grouped).forEach(category => {
    grouped[category].sort((a, b) => a.order - b.order);
  });
  
  return grouped;
}; 